import { useCallback, useRef, useState } from 'react';
import isEqual from 'react-fast-compare';

/**
 * Custom hook that creates local state from a prop,
 * and resets the state whenever the prop changes.
 * Optimized to avoid unnecessary state updates and re-renders.
 */
export const useComputedState = <T>(propValue: T) => {
  const [state, setStateInternal] = useState(propValue);
  const propRef = useRef(propValue);

  // Check if prop has changed and update state if necessary
  if (propRef.current !== propValue) {
    propRef.current = propValue;
    if (!isEqual(state, propValue)) {
      setStateInternal(propValue);
    }
  }

  // Memoized setState to prevent unnecessary re-renders of components using this hook
  // Uses functional update to avoid depending on state, providing a stable function reference
  const setState = useCallback((newValue: T | ((prevState: T) => T)) => {
    setStateInternal((currentState) => {
      const nextValue =
        typeof newValue === 'function'
          ? (newValue as (prevState: T) => T)(currentState)
          : newValue;
      // Return same reference if equal - prevents React from triggering re-render
      if (isEqual(nextValue, currentState)) {
        return currentState;
      }
      return nextValue;
    });
  }, []);

  return [state, setState] as const;
};

/**
 * Custom hook that creates local state from a prop,
 * and sets the state to the first non-null prop value.
 * After the first non-null value is set, subsequent prop changes are ignored.
 * @param propValue - The prop value to track
 * @param defaultvalue - The default value to use when propValue is null/undefined
 * @param skipDefaultOnNull - If true, don't count propValue as "set" if it equals the default value
 */
export const useComputedStateFirstNotNull = <T>(
  propValue: T | undefined,
  defaultvalue: T,
  skipDefaultOnNull = false
) => {
  const [state, setStateInternal] = useState<T>(propValue ?? defaultvalue);
  const isSetRef = useRef(false);

  // Set state only on the first non-null prop value
  if (!isSetRef.current && propValue != null) {
    // If skipDefaultOnNull is true, don't count propValue if it equals defaultvalue
    const shouldSkip = skipDefaultOnNull && isEqual(propValue, defaultvalue);

    if (!shouldSkip) {
      isSetRef.current = true;
      if (!isEqual(state, propValue)) {
        setStateInternal(propValue);
      }
    }
  }

  // Memoized setState to prevent unnecessary re-renders of components using this hook
  // Uses functional update to avoid depending on state, providing a stable function reference
  const setState = useCallback((newValue: T | ((prevState: T) => T)) => {
    setStateInternal((currentState) => {
      const nextValue =
        typeof newValue === 'function'
          ? (newValue as (prevState: T) => T)(currentState)
          : newValue;
      // Return same reference if equal - prevents React from triggering re-render
      if (isEqual(nextValue, currentState)) {
        return currentState;
      }
      return nextValue;
    });
  }, []);

  return [state, setState] as const;
};
